import { CAlert, CBadge, CCard, CCardBody, CCol, CRow, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ToastComponent from 'src/components/common/TaostComponent'
import ScorecardService from 'src/service/ScorecardService'
import Loader from 'src/components/Loader'
import moment from 'moment'

const formatDate = (date) => (date ? moment(date).format('D.MM.YYYY') : '—')
const formatTime = (time) => (time ? moment(time, ['HH:mm']).format('h:mm A') : '—')

const dropdownCol1 = [
  { key: 'rs' },
  { key: 'bf' },
  { key: 'fours' },
  { key: 'sixes' },
  { key: 'overs' },
  { key: 'mdns' },
  { key: 'run' },
  { key: 'wks' },
  { key: 'cs' },
  { key: 'cwks' },
  { key: 'sts' },
  { key: 'rods' },
  { key: 'roas' },
]
const dropdownCol2 = [{ key: 'dks' }, { key: 'hattrick' }, { key: 'no' }]
const dropdownCol3 = [{ key: 'hwot' }]
const dropdownCol4 = [
  { key: 'wb' },
  { key: 'wct' },
  { key: 'wcwk' },
  { key: 'wlbw' },
  { key: 'wst' },
  { key: 'whw' },
  { key: 'dotp' },
  { key: 'dotb' },
]

const hwotOption = {
  hwot: 'Bowled',
  hoct: 'Catch',
  hocwk: 'Catch WK',
  holbw: 'LBW',
  host: 'Stump',
  horod: 'RO-Direct',
  horoa: 'RO-Assist',
  hohw: 'Hit Wicket',
  hohb: 'Handled-Ball',
  hoobs: 'Obstruct',
  hotmd: 'Timed',
}

const ShowScorecard = () => {
  const [scorecardDetails, setScorecardDetails] = useState()
  const [fixtureDetails, setFixtureDetails] = useState({})
  const [loader, setLoader] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [gameDetails, setGameDetails] = useState({})
  const [scorecardColoumns, setScorecardColoumns] = useState([])
  const param = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    setLoader(true)
    setLoadError(false)
    ScorecardService.showScorecard(param.fixtureId)
      .then((res) => {
        if (res.status === 200) {
          setScorecardDetails(res.data)
          setFixtureDetails(res.fixture_data)
          setGameDetails(res.gameDetails)
          setScorecardColoumns(res.scorecardColoumns)
        } else {
          setLoadError(true)
        }
      })
      .catch(() => {
        ToastComponent('Something went wrong. Please try again.', 'error')
        setLoadError(true)
      })
      .finally(() => setLoader(false))
  }, [param.fixtureId])

  const hasScorecardDetails =
    fixtureDetails.scorcard_link_name !== 0 ||
    fixtureDetails?.potm_match_status !== 0 ||
    !!fixtureDetails?.fall_of_wicket ||
    !!fixtureDetails?.match_report

  // Teams-table styling: one CSmartTable inside `.ge-datatable`, columns/scoped
  // renderers built from the API's dynamic stat-column list rather than a fixed
  // array. Same four buckets the old markup used to decide how a value prints.
  const tableColumns = [
    { key: 'player_name', label: 'Player', filter: false, sorter: true, _style: { width: '20%' } },
    { key: 'fantasy_points', label: 'FP', filter: false, sorter: true, _style: { width: '8%' } },
    ...scorecardColoumns.map((value) => ({
      key: value?.attribute_key,
      label: value?.label,
      filter: false,
      sorter: true,
    })),
  ]

  const tableScopedColumns = {
    player_name: (item) => <td className="fw-semibold">{item.player_name}</td>,
    fantasy_points: (item) => <td>{item.fantasy_points}</td>,
    ...scorecardColoumns.reduce((acc, value) => {
      const key = value?.attribute_key
      if (dropdownCol1.some((dropdown) => dropdown.key === key)) {
        acc[key] = (item) => <td>{item[key]}</td>
      } else if (dropdownCol2.some((dropdown) => dropdown.key === key)) {
        acc[key] = (item) => <td>{item[key] ? 'Yes' : 'No'}</td>
      } else if (dropdownCol3.some((dropdown) => dropdown.key === key)) {
        acc[key] = (item) => <td>{hwotOption[item.hwot]}</td>
      } else if (dropdownCol4.some((dropdown) => dropdown.key === key)) {
        acc[key] = (item) => <td>{item[key] === 0 ? 'No' : 'Yes'}</td>
      }
      return acc
    }, {}),
  }

  return (
    <>
      <div className="ge-page-header">
        <button
          type="button"
          className="ge-back-link"
          onClick={() => navigate('/completed-fixtures')}
        >
          <CIcon icon={cilArrowLeft} />
          Back to Completed Fixtures
        </button>
        <h1 className="ge-page-title">
          Scorecard {fixtureDetails.team_name && <>&mdash; {fixtureDetails.team_name}</>}
        </h1>
        <p className="ge-page-subtitle">Player-by-player stats recorded for this fixture.</p>
      </div>

      <div className="ge-meta-strip">
        <div className="ge-meta-item">
          <span className="ge-meta-label">Round</span>
          <span className="ge-meta-value">{fixtureDetails.round ?? '—'}</span>
        </div>
        <div className="ge-meta-item">
          <span className="ge-meta-label">Team</span>
          <span className="ge-meta-value">{fixtureDetails.team_name ?? '—'}</span>
        </div>
        <div className="ge-meta-item">
          <span className="ge-meta-label">Starts</span>
          <span className="ge-meta-value">
            {formatDate(fixtureDetails.start_date)}, {formatTime(fixtureDetails.start_time)}
          </span>
        </div>
        <div className="ge-meta-item">
          <span className="ge-meta-label">Ends</span>
          <span className="ge-meta-value">
            {formatDate(fixtureDetails.end_date)}, {formatTime(fixtureDetails.end_time)}
          </span>
        </div>
      </div>

      <CCard className="mb-3">
        <CCardBody>
          <h5 className="mb-3">Scorecard</h5>
          {loader ? (
            <Loader />
          ) : loadError ? (
            <CAlert color="danger" className="mb-0">
              Could not load this scorecard. Please try again.
            </CAlert>
          ) : gameDetails.game_type != 3 ? (
            scorecardDetails && scorecardDetails.length > 0 ? (
              <div className="ge-datatable ge-datatable--static">
                <CSmartTable
                  columns={tableColumns}
                  scopedColumns={tableScopedColumns}
                  items={scorecardDetails}
                  tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                />
              </div>
            ) : (
              <div className="text-medium-emphasis text-center py-4">
                No scorecard entries recorded for this fixture yet.
              </div>
            )
          ) : (
            <div className="text-medium-emphasis text-center py-4">
              Scorecard view isn&apos;t available for this match type.
            </div>
          )}
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody>
          <h5 className="mb-3">Scorecard Details</h5>
          {!loader && !loadError && !hasScorecardDetails && (
            <div className="text-medium-emphasis">No additional match details recorded yet.</div>
          )}
          <CRow className="g-3">
            {fixtureDetails.scorcard_link_name !== 0 && (
              <CCol md={12}>
                <div className="text-medium-emphasis small">Scorecard Source</div>
                <a
                  href={fixtureDetails?.scorcard_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {fixtureDetails?.scorcard_link_name}
                </a>
              </CCol>
            )}

            {fixtureDetails?.potm_match_status !== 0 && (
              <CCol md={12}>
                <div className="text-medium-emphasis small">Match Result</div>
                <CBadge
                  color={fixtureDetails?.potm_match_status === 1 ? 'success' : 'secondary'}
                  shape="rounded-pill"
                  className="px-2 py-1"
                >
                  {fixtureDetails?.potm_match_status === 1 ? 'Won' : 'Other'}
                </CBadge>
              </CCol>
            )}
            {fixtureDetails?.fall_of_wicket && (
              <CCol md={12}>
                <div className="text-medium-emphasis small">Fall of Wickets</div>
                <div>{fixtureDetails?.fall_of_wickets}</div>
              </CCol>
            )}
            {fixtureDetails?.match_report && (
              <CCol md={12}>
                <div className="text-medium-emphasis small">Match Report</div>
                <div>{fixtureDetails?.match_report}</div>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>
    </>
  )
}

export default ShowScorecard
